import { FileEntity } from '../../types';

const IMAGE_EXTENSIONS = new Set([
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'bmp',
  'svg',
  'tif',
  'tiff',
]);

export interface ReportRow {
  key: string;
  dateiname: string;
  files: FileEntity[];
  word?: FileEntity;
  pdf?: FileEntity;
}

function basename(dateiname: string): string {
  const slash = Math.max(dateiname.lastIndexOf('/'), dateiname.lastIndexOf('\\'));
  return slash >= 0 ? dateiname.slice(slash + 1) : dateiname;
}

function extensionOf(dateiname: string): string {
  const base = basename(dateiname);
  const dot = base.lastIndexOf('.');
  if (dot <= 0) return '';
  return base.slice(dot + 1).toLowerCase();
}

function stemOf(dateiname: string): string {
  const base = basename(dateiname);
  const dot = base.lastIndexOf('.');
  if (dot <= 0) return base;
  return base.slice(0, dot);
}

/**
 * Schwachstellen-Dokumentation, die über die Audit-Dateiliste in die Reports rutscht.
 *
 * Bedingung: GET /api/files/?audit_id= filtert nur File.audit_id. Seed-Screenshots
 * (und Bearbeitungen davon) setzen zusätzlich schwachstelle_id und typ "Screenshot"
 * und liegen als PNG unter UPLOAD_DIR (backend/uploads, screenshot_sv*.png).
 */
export function isSchwachstellenDokumentationsbild(file: FileEntity): boolean {
  if (file.schwachstelle_id == null) return false;
  const typ = (file.typ ?? '').trim().toLowerCase();
  return typ === 'screenshot' || IMAGE_EXTENSIONS.has(extensionOf(file.dateiname));
}

/** Rohdateien aus Fortinet-/Firewall-Uploads (backend: File.typ „FirewallPolicy“). */
export function isFirewallExportFile(file: FileEntity): boolean {
  return (file.typ ?? '').trim().toLowerCase() === 'firewallpolicy';
}

export function isAuditReportListFile(file: FileEntity): boolean {
  return !isSchwachstellenDokumentationsbild(file) && !isFirewallExportFile(file);
}

function pairKey(dateiname: string): string {
  return stemOf(dateiname).toLowerCase();
}

/**
 * Word und PDF einer Generierung teilen denselben Dateistamm
 * (Ergebnisbericht_{audit}_{zeitstempel} bzw. results-report_{audit}_{zeitstempel}).
 * Einzelne docx- oder pdf-Dateien bleiben eine eigene Zeile.
 */
export function buildReportRows(files: FileEntity[]): ReportRow[] {
  const visible = files.filter(isAuditReportListFile);
  const consumed = new Set<number>();
  const byStem = new Map<string, FileEntity[]>();

  for (const file of visible) {
    const key = pairKey(file.dateiname);
    const group = byStem.get(key) ?? [];
    group.push(file);
    byStem.set(key, group);
  }

  const rows: ReportRow[] = [];
  const seen = new Set<string>();

  for (const file of visible) {
    const key = pairKey(file.dateiname);
    if (seen.has(key)) continue;
    seen.add(key);

    const group = byStem.get(key) ?? [];
    const word = group.find((entry) => extensionOf(entry.dateiname) === 'docx');
    const pdf = group.find((entry) => extensionOf(entry.dateiname) === 'pdf');

    if (word && pdf) {
      consumed.add(word.id);
      consumed.add(pdf.id);
      rows.push({
        key: `${word.id}-${pdf.id}`,
        dateiname: stemOf(word.dateiname),
        files: [word, pdf],
        word,
        pdf,
      });
    }

    for (const entry of group) {
      if (consumed.has(entry.id)) continue;
      consumed.add(entry.id);
      const extension = extensionOf(entry.dateiname);
      rows.push({
        key: String(entry.id),
        dateiname: entry.dateiname,
        files: [entry],
        word: extension === 'docx' ? entry : undefined,
        pdf: extension === 'pdf' ? entry : undefined,
      });
    }
  }

  return rows;
}

export function reportRowFileIds(row: ReportRow): number[] {
  return row.files.map((file) => file.id);
}
